/**
 * Текст документа из админки — HTML её редактора. На страницу он попадает
 * только через этот фильтр: пропускаем то, что умеет редактор, остальное
 * (скрипты, фреймы, обработчики событий, `javascript:`-ссылки, произвольные
 * стили) вырезаем. Свой экземпляр DOMPurify — чтобы наши хуки не достались
 * другим местам, где он может понадобиться.
 */
import DOMPurify from 'dompurify'
import { mediaUrl } from '@dorixona/api'

/** Теги редактора админки. */
export const ALLOWED_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'mark',
  'span',
  'ul',
  'ol',
  'li',
  'blockquote',
  'hr',
  'br',
  'a',
  'img',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
]

/** Атрибуты; `target`, `rel` и `loading` ставит сам фильтр. */
export const ALLOWED_ATTR = ['href', 'src', 'alt', 'style', 'start', 'colspan', 'rowspan']

/** Ссылки — только сайты, почта и телефон; картинки — только https. */
const ALLOWED_URI = /^(?:https?:|mailto:|tel:)/i
/**
 * То же плюс пути приложения (`/catalog`). `//host` и `/\host` браузер читает
 * как чужой сайт без схемы — такие не проходят.
 */
const ALLOWED_URI_INTERNAL = /^(?:https?:|mailto:|tel:|\/(?![/\\]))/i
const EXTERNAL = /^https?:\/\//i
const HTTPS = /^https:\/\//i
/** Файл, загруженный на наш сервер: путь от корня API, как у фото товаров. */
const MEDIA = /^\/media\//

const COLOR = /^(?:#[0-9a-f]{3,8}|(?:rgba?|hsla?)\([\w\s.,%/+-]*\)|[a-z]+)$/i
const ALIGN = /^(?:left|right|center|justify|start|end)$/i

const TEXT_STYLE = new Map([
  ['color', COLOR],
  ['background-color', COLOR],
])
const BLOCK_STYLE = new Map([['text-align', ALIGN]])
const NO_STYLE = new Map<string, RegExp>()

/** Какие свойства `style` можно какому тегу: цвет у текста, выравнивание у абзаца. */
const STYLES = new Map([
  ['SPAN', TEXT_STYLE],
  ['MARK', TEXT_STYLE],
  ['P', BLOCK_STYLE],
  ['H1', BLOCK_STYLE],
  ['H2', BLOCK_STYLE],
  ['H3', BLOCK_STYLE],
])

/**
 * Оставляет в `style` только разрешённые тегу свойства с понятными значениями:
 * `url(...)`, `expression(...)`, позиционирование и размеры не проходят.
 */
export function filterStyle(tag: string, style: string): string {
  const allowed = STYLES.get(tag.toUpperCase()) ?? NO_STYLE
  const kept: string[] = []
  for (const declaration of style.split(';')) {
    const colon = declaration.indexOf(':')
    if (colon < 0) continue
    const property = declaration.slice(0, colon).trim().toLowerCase()
    const value = declaration.slice(colon + 1).trim()
    if (allowed.get(property)?.test(value)) kept.push(`${property}: ${value}`)
  }
  return kept.join('; ')
}

const purify = DOMPurify(window)

/**
 * Адрес API для картинок `/media/…` на время одного вызова `sanitizeHtml`:
 * хуки DOMPurify общие, параметров вызова они не видят. null — такие
 * картинки не пускаем (документы открываются и вне приложения).
 */
let mediaBase: string | null = null

purify.addHook('uponSanitizeAttribute', (node, event) => {
  if (event.attrName !== 'style') return
  const style = filterStyle(node.nodeName, event.attrValue)
  if (style) event.attrValue = style
  else event.keepAttr = false
})

purify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A' && EXTERNAL.test(node.getAttribute('href') ?? '')) {
    // Запасной путь: экран сам открывает такие ссылки через openOutside.
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
  if (node.nodeName === 'IMG') {
    const src = node.getAttribute('src') ?? ''
    if (mediaBase !== null && MEDIA.test(src)) {
      node.setAttribute('src', mediaUrl(src, mediaBase))
      node.setAttribute('loading', 'lazy')
    } else if (HTTPS.test(src)) node.setAttribute('loading', 'lazy')
    else node.remove()
  }
})

export interface SanitizeOptions {
  /**
   * Оставить ссылки на пути приложения (`/stores`). Экран, который так делает,
   * сам ведёт их через роутер. Документы (политика) их не пускают: страница
   * открывается и вне приложения, где такой путь никуда не ведёт.
   */
  internalLinks?: boolean
  /**
   * Пускать картинки, загруженные на наш сервер (`/media/…`), достраивая их
   * адрес до домена API — как `mediaUrl` у фото товаров. Работает вместе с
   * `internalLinks`: без него путь от корня отсекает сам DOMPurify.
   */
  mediaBase?: string
}

/**
 * Чистый HTML для `dangerouslySetInnerHTML`. Заголовок страницы — единственный
 * h1, поэтому h1 из текста становится h2: это раздел документа.
 */
export function sanitizeHtml(html: string, options: SanitizeOptions = {}): string {
  mediaBase = options.mediaBase ?? null
  const fragment = purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: options.internalLinks ? ALLOWED_URI_INTERNAL : ALLOWED_URI,
    // Числа в этих атрибутах — не адреса: без этого их отсёк бы ALLOWED_URI.
    ADD_URI_SAFE_ATTR: ['start', 'colspan', 'rowspan'],
    // data-* и aria-* DOMPurify по умолчанию пускает; редактору они не нужны.
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  })
  mediaBase = null
  for (const h1 of Array.from(fragment.querySelectorAll('h1'))) {
    const h2 = h1.ownerDocument.createElement('h2')
    for (const attr of Array.from(h1.attributes)) h2.setAttribute(attr.name, attr.value)
    h2.append(...Array.from(h1.childNodes))
    h1.replaceWith(h2)
  }
  const box = fragment.ownerDocument.createElement('div')
  box.append(fragment)
  return box.innerHTML
}

/** В тексте нет ни одного тега — его написали простым текстом. */
export function isPlainText(body: string): boolean {
  return !/<[a-z][^>]*>/i.test(body)
}

/** Простой текст — абзацами по пустым строкам. */
export function paragraphs(body: string): string[] {
  return body
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
}
